import axios from '../../../axios';
import type { TtCourse, TtStudentRecord, TtStudentDocument, TtCourseFormData, TtRecordFormData, TtStats, CourseStatus, LicenseClass, DocType, DocDetectResult, DvhcItem, CsdtStatus, DvhcConvertResult, DvhcConvertStatus } from '../types';

type ApiRes<T> = { EC: number; EM: string; DT: T };

// ── Courses ─────────────────────────────────────────────────────────────────

export const fetchCourses = (params?: { status?: CourseStatus; hang?: LicenseClass }) =>
  axios.get<ApiRes<TtCourse[]>>('/api/trung-tam/courses', { params }).then(r => r.data);

export const fetchCourse = (id: number) =>
  axios.get<ApiRes<TtCourse>>(`/api/trung-tam/courses/${id}`).then(r => r.data);

export const createCourseApi = (data: TtCourseFormData) =>
  axios.post<ApiRes<TtCourse>>('/api/trung-tam/courses', data).then(r => r.data);

export const updateCourseApi = (id: number, data: Partial<TtCourseFormData & { status: CourseStatus }>) =>
  axios.put<ApiRes<TtCourse>>(`/api/trung-tam/courses/${id}`, data).then(r => r.data);

export const deleteCourseApi = (id: number) =>
  axios.delete<ApiRes<null>>(`/api/trung-tam/courses/${id}`).then(r => r.data);

// ── Student Records ─────────────────────────────────────────────────────────

export type RecordsPage = {
  records: TtStudentRecord[];
  total: number;
  page: number;
  limit: number;
};

export const fetchRecords = (courseId: number, page = 1, limit = 50) =>
  axios
    .get<ApiRes<RecordsPage>>(`/api/trung-tam/courses/${courseId}/records`, { params: { page, limit } })
    .then(r => r.data);

/** Fetch a single record with all heavy fields (base64 images + documents).
 *  Call only when the operator clicks a row to open the detail panel. */
export const fetchRecordDetail = (id: number) =>
  axios.get<ApiRes<TtStudentRecord>>(`/api/trung-tam/records/${id}`).then(r => r.data);

export const createRecordApi = (data: TtRecordFormData) =>
  axios.post<ApiRes<TtStudentRecord>>('/api/trung-tam/records', data).then(r => r.data);

export const updateRecordApi = (id: number, data: Partial<TtRecordFormData>) =>
  axios.put<ApiRes<TtStudentRecord>>(`/api/trung-tam/records/${id}`, data).then(r => r.data);

export const deleteRecordApi = (id: number) =>
  axios.delete<ApiRes<null>>(`/api/trung-tam/records/${id}`).then(r => r.data);

// ── Review & Transfer ──────────────────────────────────────────────────────

export const toggleReviewedApi = (id: number, reviewed: boolean) =>
  axios.patch<ApiRes<TtStudentRecord>>(`/api/trung-tam/records/${id}/reviewed`, { reviewed }).then(r => r.data);

export const updateCsdtStatusApi = (id: number, csdtStatus: CsdtStatus) =>
  axios.patch<ApiRes<{ id: number; csdtStatus: CsdtStatus }>>(`/api/trung-tam/records/${id}/csdt-status`, { csdtStatus }).then(r => r.data);

export const bulkTransferApi = (recordIds: number[], targetCourseId: number) =>
  axios.post<ApiRes<{ transferred: number }>>('/api/trung-tam/records/bulk-transfer', { recordIds, targetCourseId }).then(r => r.data);

export type TransferTargetDto = {
  id: number;
  name: string;
  hang: string;
  startDate: string | null;
  status: string;
  maxReviewed: number | null;
};

/**
 * List OPEN courses with the same hang as the source course —
 * populates the "Chuyển khóa" modal picklist. Mirrors the backend
 * eligibility rule exactly so the UI never offers an invalid target.
 */
export const getTransferTargetsApi = (hang: string, excludeCourseId?: number) =>
  axios.get<ApiRes<TransferTargetDto[]>>('/api/trung-tam/courses/transfer-targets', {
    params: { hang, excludeCourseId },
  }).then(r => r.data);

// ── Student Documents ──────────────────────────────────────────────────────

export const uploadDocumentApi = (recordId: number, file: File, docType: DocType, label?: string) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('docType', docType);
  if (label) fd.append('label', label);
  return axios.post<ApiRes<TtStudentDocument>>(
    `/api/trung-tam/records/${recordId}/documents`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ).then(r => r.data);
};

export const deleteDocumentApi = (docId: number) =>
  axios.delete<ApiRes<null>>(`/api/trung-tam/documents/${docId}`).then(r => r.data);

// ── Stats ───────────────────────────────────────────────────────────────────

export const fetchStats = () =>
  axios.get<ApiRes<TtStats>>('/api/trung-tam/stats').then(r => r.data);

// ── ĐVHC (Administrative Units) ────────────────────────────────────────

export const searchDvhcApi = (q: string, limit = 20) =>
  axios.get<ApiRes<DvhcItem[]>>('/api/dvhc/search', { params: { q, limit } }).then(r => r.data);

export const lookupDvhcApi = (ma: string) =>
  axios.get<ApiRes<DvhcItem>>('/api/dvhc/lookup', { params: { ma } }).then(r => r.data);

// ── DVHC Convert ───────────────────────────────────────────────────────────

export const bulkConvertDvhcApi = (courseId: number) =>
  axios
    .post<ApiRes<DvhcConvertResult>>(`/api/trung-tam/courses/${courseId}/dvhc/convert-batch`)
    .then(r => r.data);

export const convertOneDvhcApi = (recordId: number) =>
  axios
    .post<ApiRes<{ record: TtStudentRecord; source: string; notSure: boolean }>>(
      `/api/trung-tam/records/${recordId}/dvhc/convert-one`,
    )
    .then(r => r.data);

/**
 * Stateless DVHC convert — takes a raw address string and returns the
 * resolved ward + province without touching the DB. Used by the edit
 * modal for live auto-convert while the user is typing.
 */
export const convertDvhcTextApi = (diaChi: string) =>
  axios
    .post<ApiRes<{ maDvhc: string; tenDayDu: string; source: string; notSure: boolean }>>(
      '/api/trung-tam/dvhc/convert-text',
      { diaChi },
    )
    .then(r => r.data);

// ── Face recognition ───────────────────────────────────────────────────────

export type PortraitCheckResult = {
  isPortrait: boolean;
  faceCount: number;
  largestFaceRatio: number;
  sharpness: number;
  reason: string | null;
};

export type FaceCompareResult = {
  similarity: number;
  verified: boolean;
  distance: number;
  threshold: number;
  model: string;
  durationMs: number;
};

/** Classify whether an uploaded image is a valid portrait (1 clear face). */
export const checkPortraitApi = (imageBase64: string) =>
  axios
    .post<ApiRes<PortraitCheckResult>>('/api/trung-tam/face/check-portrait', { imageBase64 })
    .then(r => r.data);

/** Compare two faces and get a 0-100 similarity score. */
export const compareFacesApi = (image1Base64: string, image2Base64: string) =>
  axios
    .post<ApiRes<FaceCompareResult>>('/api/trung-tam/face/compare', { image1Base64, image2Base64 })
    .then(r => r.data);

/** Persist the uploaded portrait + score onto a record. */
export const updatePortraitApi = (
  recordId: number,
  payload: {
    portraitBase64: string | null;
    /** Raw selfie before AI enhance. Omit to keep the existing original. */
    portraitOriginalBase64?: string | null;
    /** JSON array of all AI variants. Omit to keep existing. */
    portraitAiVariantsJson?: string | null;
    faceMatchScore: number | null;
  },
) =>
  axios
    .put<ApiRes<TtStudentRecord>>(`/api/trung-tam/records/${recordId}/portrait`, payload)
    .then(r => r.data);

export type EnhancePortraitResult = {
  enhancedBase64: string;
  mimeType: string;
  detectedGender: 'nam' | 'nu' | 'auto';
};

/**
 * Transform an uploaded selfie into a 3×4 Vietnamese ID portrait via
 * Gemini (blue background, collared shirt, light beautification, face
 * geometry preserved). Server returns EC=1 on model failure with the
 * caller expected to fall back to the original image.
 */
export const enhancePortraitApi = (payload: {
  imageBase64: string;
  gender?: 'nam' | 'nu' | 'auto';
  mimeType?: string;
}) =>
  axios
    .post<ApiRes<EnhancePortraitResult>>('/api/trung-tam/portrait/enhance', payload)
    .then(r => r.data);

// ── Teacher → Center submission queue ─────────────────────────────────────

export type SubmissionStatus = 'pending' | 'accepted' | 'entered' | 'rejected' | 'deferred';
export type SubmissionScanMethod = 'qr' | 'ocr' | 'manual';

export type TtSubmission = {
  id: number;
  submittedBy: number;
  submittedAt: string;
  // Queue ordinal scoped to (submittedBy, hang). Deferred rows preserve
  // their seqNo so they float back to the top for the next matching course.
  seqNo: number;
  // Defer metadata — set when SupperNVNL/NVNL pushes the record to a later
  // course. status='deferred' goes with these.
  deferredAt: string | null;
  deferredBy: number | null;
  deferredFromCourseId: number | null;
  deferrer?: { id: number; username: string } | null;
  // How the teacher acquired this data. "qr" is trusted (decoded from the
  // CCCD QR chip); "ocr" may contain recognition errors; "manual" was typed.
  scanMethod: SubmissionScanMethod;
  soCCCD: string | null;
  soCMT: string | null;
  hoTen: string;
  ngaySinh: string | null;
  gioiTinh: string | null;
  diaChi: string | null;
  ngayCap: string | null;
  avatarBase64: string | null;
  portraitBase64: string | null;
  sourceImageBase64: string | null;
  faceMatchScore: number | null;
  hang: string;
  status: SubmissionStatus;
  acceptedBy: number | null;
  acceptedAt: string | null;
  enteredBy: number | null;
  enteredAt: string | null;
  linkedRecordId: number | null;
  linkedCourseId: number | null;
  rejectedBy: number | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  note: string | null;
  // Extra documents JSON (GPLX, health cert, misc) — same shape as
  // tt_pending_record/tt_student_record extraImagesJson. Parse to render
  // in the review modal's "Giấy tờ đính kèm" gallery.
  extraImagesJson: string | null;
  // DVHC pre-resolved at submit time so the review UI can render the
  // ĐVHC cấp 2 chip without needing another lookup.
  maDvhc: string | null;
  dvhcTenDayDu: string | null;
  // Course this row is queued for (set at submit time by oldest-open-course
  // lookup, may differ from linkedCourseId if NVTT later enters elsewhere).
  ttCourseId: number | null;
  createdAt: string;
  updatedAt: string;
  submitter?: { id: number; username: string } | null;
  acceptor?: { id: number; username: string } | null;
  enterer?: { id: number; username: string } | null;
  rejecter?: { id: number; username: string } | null;
  linkedCourse?: { id: number; name: string; hang: string; status: string } | null;
};

export type SubmissionPayload = {
  // "qr" | "ocr" | "manual" — how the teacher got this record. Defaults
  // server-side to "ocr" when omitted.
  scanMethod?: SubmissionScanMethod;
  soCCCD?: string | null;
  soCMT?: string | null;
  hoTen: string;
  ngaySinh?: string | null;
  gioiTinh?: string | null;
  diaChi?: string | null;
  ngayCap?: string | null;
  avatarBase64?: string | null;
  portraitBase64?: string | null;
  sourceImageBase64?: string | null;
  faceMatchScore?: number | null;
  // DVHC pre-resolved by the scan form's auto-convert effect. When
  // present, the backend persists these on the queue row so NVTT
  // doesn't have to re-convert on the enter step.
  maDvhc?: string | null;
  dvhcTenDayDu?: string | null;
  // Extra documents uploaded alongside the CCCD (GPLX, health cert, etc.).
  // Stored as JSON in the queue row and converted to tt_student_document
  // records when NVTT enters the submission into a course.
  extraImages?: { base64: string; docType: string; label?: string }[];
  hang: string;
  note?: string | null;
};

/** Teacher side — submit a new record into the queue. */
export const teacherSubmitApi = (payload: SubmissionPayload) =>
  axios
    .post<ApiRes<TtSubmission>>('/api/teacher/submissions', payload)
    .then(r => r.data);

/** Teacher side — patch a pending submission. Re-allocates seqNo per
 *  Phương án X (record moves to end of new target queue, old slot
 *  empty). Only allowed while status='pending'. */
export const teacherUpdateSubmissionApi = (id: number, patch: Partial<SubmissionPayload>) =>
  axios
    .put<ApiRes<TtSubmission>>(`/api/teacher/submissions/${id}`, patch)
    .then(r => r.data);

/** Teacher side — list all submissions owned by the current teacher. */
export const teacherListSubmissionsApi = (status?: SubmissionStatus) =>
  axios
    .get<ApiRes<TtSubmission[]>>('/api/teacher/submissions', { params: status ? { status } : undefined })
    .then(r => r.data);

/** Teacher side — merged history (own submissions + records the
 *  center entered on this teacher's behalf). */
export type TeacherHistoryDirectRecord = {
  id: number;
  ttCourseId: number | null;
  seqNo: number | null;
  hoTen: string;
  soCCCD: string | null;
  ngaySinh: string | null;
  reviewed: boolean;
  csdtStatus: string;
  createdAt: string;
  course?: { id: number; name: string; hang: string; status: string } | null;
  enteredByUser?: { id: number; username: string } | null;
};
export type TeacherHistoryResponse = {
  submissions: TtSubmission[];
  directRecords: TeacherHistoryDirectRecord[];
};
export const teacherHistoryApi = () =>
  axios
    .get<ApiRes<TeacherHistoryResponse>>('/api/teacher/history')
    .then(r => r.data);

/** Teacher side — retract a pending submission. */
export const teacherDeleteSubmissionApi = (id: number) =>
  axios
    .delete<ApiRes<null>>(`/api/teacher/submissions/${id}`)
    .then(r => r.data);

/** Center side — list submissions in the queue with filters. */
export const listSubmissionQueueApi = (params: { status?: SubmissionStatus; hang?: string } = {}) =>
  axios
    .get<ApiRes<TtSubmission[]>>('/api/trung-tam/queue', { params })
    .then(r => r.data);

/** Center side — count of pending submissions (for sidebar badge). */
export const getQueuePendingCountApi = () =>
  axios
    .get<ApiRes<{ count: number }>>('/api/trung-tam/queue/pending-count')
    .then(r => r.data);

/** Center side — auto-accept all pending submissions (called when NVTT opens queue tab). */
export const batchAcceptSubmissionsApi = () =>
  axios
    .post<ApiRes<{ accepted: number }>>('/api/trung-tam/queue/batch-accept')
    .then(r => r.data);

/** Center side — accept a pending submission. */
export const acceptSubmissionApi = (id: number) =>
  axios
    .post<ApiRes<TtSubmission>>(`/api/trung-tam/queue/${id}/accept`)
    .then(r => r.data);

/** Center side — reject a submission with a required reason. */
export const rejectSubmissionApi = (id: number, reason: string) =>
  axios
    .post<ApiRes<TtSubmission>>(`/api/trung-tam/queue/${id}/reject`, { reason })
    .then(r => r.data);

/** Center side — enter an accepted submission into a real course. */
export const enterSubmissionApi = (
  id: number,
  payload: { courseId: number; overrides?: Partial<SubmissionPayload> & { maDvhc?: string; dvhcTenDayDu?: string } },
) =>
  axios
    .post<ApiRes<{ record: TtStudentRecord; queue: TtSubmission }>>(
      `/api/trung-tam/queue/${id}/enter`,
      payload,
    )
    .then(r => r.data);

/** Center side — push a record to the next matching course. Preserves seqNo. */
export const deferSubmissionApi = (id: number) =>
  axios
    .post<ApiRes<TtSubmission>>(`/api/trung-tam/queue/${id}/defer`)
    .then(r => r.data);

/** Center side — undo a defer (back to 'accepted'). */
export const undeferSubmissionApi = (id: number) =>
  axios
    .post<ApiRes<TtSubmission>>(`/api/trung-tam/queue/${id}/undefer`)
    .then(r => r.data);

// ── SupperNhanVienNhapLieu — quota allocation ─────────────────────────────

export type SupperNvnlQuota = {
  id: number;
  maxRecords: number;
  allocatedAt: string;
  allocatedBy: { id: number; username: string };
  note: string | null;
};

export type SupperNvnlTeacherBucket = {
  submittedBy: number;
  hang: string;
  submitter: { id: number; username: string; avatarUrl: string | null } | null;
  totalActive: number;
  pendingCount: number;
  acceptedCount: number;
  deferredCount: number;
  oldestSubmittedAt: string;
  oldestSeqNo: number;
  quota: SupperNvnlQuota | null;
  enteredCount: number;
};

export const supperQueueByTeacherApi = (params: { courseId?: number; hang?: string } = {}) =>
  axios
    .get<ApiRes<SupperNvnlTeacherBucket[]>>('/api/supper-nvnl/queue/by-teacher', { params })
    .then(r => r.data);

export const supperQueueForTeacherApi = (teacherId: number, params: { hang?: string } = {}) =>
  axios
    .get<ApiRes<TtSubmission[]>>(`/api/supper-nvnl/queue/by-teacher/${teacherId}`, { params })
    .then(r => r.data);

export const supperAllocateQuotaApi = (payload: {
  teacherId: number;
  courseId: number;
  hang: string;
  maxRecords: number;
  note?: string;
}) =>
  axios
    .post<ApiRes<{ id: number }>>('/api/supper-nvnl/quota', payload)
    .then(r => r.data);

export const supperDeallocateQuotaApi = (quotaId: number) =>
  axios
    .delete<ApiRes<null>>(`/api/supper-nvnl/quota/${quotaId}`)
    .then(r => r.data);

export const supperListQuotasApi = (courseId: number) =>
  axios
    .get<ApiRes<Array<{
      id: number;
      submittedBy: number;
      courseId: number;
      hang: string;
      maxRecords: number;
      allocatedAt: string;
      note: string | null;
      teacher?: { id: number; username: string } | null;
      allocator?: { id: number; username: string } | null;
    }>>>('/api/supper-nvnl/quota', { params: { courseId } })
    .then(r => r.data);

export const getDvhcConvertStatusApi = () =>
  axios
    .get<ApiRes<DvhcConvertStatus>>('/api/trung-tam/dvhc/convert-status')
    .then(r => r.data);

// ── Document Detection ─────────────────────────────────────────────────────

/**
 * AI ID-photo generator. Takes the CCCD avatar (any format the browser can
 * fetch) and returns a polished 3:4 passport-style portrait (face composited
 * onto a suit + blue-background template via InsightFace InSwap).
 * `DT` is a bare base64 JPEG string (NOT prefixed with `data:`) on success.
 */
export const generateIdPhotoApi = (image: Blob | File, gender?: 'male' | 'female') => {
  const fd = new FormData();
  fd.append('image', image, 'avatar.jpg');
  if (gender) fd.append('gender', gender);
  return axios.post<ApiRes<string>>('/api/file/idphoto/generate', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 90000,
  }).then(r => r.data);
};

/**
 * Downscale an image to at most `maxDim` on its longest edge and re-encode
 * as JPEG ~82% quality. A phone-captured CCCD is commonly 4000×3000 (~4-8MB),
 * which is far more detail than the Mistral OCR pipeline can use (it
 * internally resizes to 1200px anyway) and stalls uploads on mobile 5G.
 * Shrinking client-side cuts upload payload 5-10x and materially improves
 * time-to-first-byte when multiple images go out back-to-back.
 */
const resizeImageForUpload = async (file: File, maxDim = 1600): Promise<File> => {
  // Fast path — skip resize for non-images or already-small files.
  if (!file.type.startsWith('image/') || file.size < 300_000) return file;
  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(new Error('FileReader failed'));
      fr.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Image decode failed'));
      i.src = dataUrl;
    });
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return file;
    const longEdge = Math.max(w, h);
    if (longEdge <= maxDim) return file;
    const scale = maxDim / longEdge;
    const tw = Math.round(w * scale);
    const th = Math.round(h * scale);
    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, tw, th);
    const blob: Blob | null = await new Promise(resolve =>
      canvas.toBlob(b => resolve(b), 'image/jpeg', 0.82),
    );
    if (!blob || blob.size >= file.size) return file;
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
};

/**
 * Type hint sent alongside the image when the operator picked a labelled
 * capture button (CCCD / GPLX / Ảnh thẻ / Khác). Backend uses the hint to
 * skip its 6k-token classify prompt — 'portrait' and 'other' short-circuit
 * without calling Mistral; 'cccd' and 'gplx' run a narrow extraction prompt
 * that's 3-5x faster than auto-classification.
 */
export type DocTypeHint = 'cccd' | 'gplx' | 'portrait' | 'other' | 'mixed';

export const detectDocumentApi = async (file: File, hint?: DocTypeHint) => {
  const compact = await resizeImageForUpload(file);
  const fd = new FormData();
  fd.append('image', compact);
  if (hint) fd.append('docType', hint);
  const r = await axios.post<ApiRes<DocDetectResult>>('/api/file/document/detect', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Detect calls Mistral serially (MAX_CONCURRENCY=1) and each call takes
    // 7-24s. When 3 images are staged together they run sequentially on the
    // backend — 30s client timeout would abort the 2nd+ requests. 120s gives
    // enough headroom for ~5 images back-to-back.
    timeout: 120000,
  });
  return r.data;
};

/**
 * Multi-image detect: send 1-2 files of the SAME document type (CCCD or
 * GPLX, front + back). Backend runs the AI orchestrator and merges the
 * fields across images so the operator gets one unified record. For
 * RoutesAI engine, both images are forwarded in a single upstream call
 * which lets Gemini merge natively; for Mistral, each image is OCR'd and
 * fields merged server-side with non-empty winning.
 */
export const detectDocumentMultiApi = async (
  files: File[],
  hint: 'cccd' | 'gplx',
) => {
  if (!files.length || files.length > 2) {
    throw new Error('detectDocumentMultiApi: cần 1-2 ảnh');
  }
  const compacted = await Promise.all(files.map((f) => resizeImageForUpload(f)));
  const fd = new FormData();
  for (const c of compacted) fd.append('image', c);
  fd.append('docType', hint);
  const r = await axios.post<ApiRes<DocDetectResult>>('/api/file/document/detect-multi', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // 2-image OCR can take up to 25s per RoutesAI spec; pad for retries.
    timeout: 180000,
  });
  return r.data;
};

/** Crop an image to tight 3x4 aspect ratio (for manual ID card photos). */
export const crop3x4Api = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return axios.post<ApiRes<string>>('/api/file/qr/crop-3x4', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  }).then(r => r.data);
};

/** Crop the face/avatar area from a CCCD image (Python YuNet/SCRFD pipeline).
 *  Returns bare base64 JPEG (no data-URL prefix), or null if no face detected.
 *  Used by the batch queue to extract a tight portrait from the uploaded CCCD
 *  so it can be compared against the student's portrait via face-match. */
export const cropFaceApi = async (file: File | Blob): Promise<ApiRes<string | null>> => {
  // Resize on the client side — the original CCCD photo is usually
  // 800KB-2MB. YuNet doesn't benefit from anything beyond ~1400px and
  // the big body used to time out over slow mobile uplinks. Shrinking
  // here cuts the wire payload 5-10x without hurting detection.
  const asFile = file instanceof File
    ? file
    : new File([file], 'cccd.jpg', { type: 'image/jpeg' });
  const compact = await resizeImageForUpload(asFile, 1400);
  const call = async () => {
    const fd = new FormData();
    fd.append('image', compact, 'cccd.jpg');
    const r = await axios.post<ApiRes<string | null>>('/api/file/qr/crop-face', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // 60s — YuNet+SCRFD warm-up can take ~10s on a cold worker and
      // Cloudflare tunnel upload adds another ~5s on 5G. 30s was too
      // tight; operators were seeing "Lỗi mạng" on slow networks.
      timeout: 60000,
    });
    return r.data;
  };
  try {
    return await call();
  } catch {
    // One retry after a short delay — covers transient tunnel stalls.
    await new Promise((res) => setTimeout(res, 800));
    try { return await call(); } catch {
      return { EC: 1, EM: 'Dịch vụ cắt ảnh tạm không sẵn sàng', DT: null };
    }
  }
};

// ── Pending records (Chờ lưu hồ sơ — persistent batch queue) ─────────

export type PendingRecordDto = {
  id: number;
  createdBy: number;
  ttCourseId: number | null;
  status: 'pending' | 'detecting' | 'done' | 'error';
  currentStep?: string | null;
  errorMsg?: string | null;
  soCCCD?: string | null;
  soCMT?: string | null;
  hoTen?: string | null;
  ngaySinh?: string | null;
  gioiTinh?: string | null;
  diaChi?: string | null;
  diaChiCap2?: string | null;
  ngayCap?: string | null;
  soGPLX?: string | null;
  hangGPLX?: string | null;
  hangGPLXMoi?: string | null;
  ngayCapGPLX?: string | null;
  ngayTrungTuyenGPLX?: string | null;
  ngayHetHanGPLX?: string | null;
  avatarBase64?: string | null;
  sourceImageBase64?: string | null;
  portraitBase64?: string | null;
  portraitOriginalBase64?: string | null;
  portraitAiVariantsJson?: string | null;
  extraImagesJson?: string | null;
  maDvhc?: string | null;
  dvhcTenDayDu?: string | null;
  faceMatchScore?: number | null;
  scanMethod?: 'qr' | 'ocr' | 'manual';
  // Per-doc source so the review UI can show a mixed badge (CCCD by QR,
  // GPLX by OCR). Falls back to `scanMethod` when the backend has no
  // dedicated columns yet — kept optional for forward compatibility.
  cccdSource?: 'qr' | 'ocr' | 'manual' | null;
  gplxSource?: 'qr' | 'ocr' | 'manual' | null;
  // Supper teacher (groupId=6) phụ trách học viên — chọn qua autocomplete
  // trên form nhập hồ sơ.
  teacherId?: number | null;
  teacher?: { id: number; username: string; email: string } | null;
  fileCount?: number;
  createdAt: string;
  updatedAt: string;
};

export type DuplicateInfo = {
  recordId: number;
  courseId: number | null;
  courseName: string | null;
  courseHang: string | null;
  courseStatus: string | null;
  hoTen: string | null;
};

export const createPendingRecordApi = (data: Partial<PendingRecordDto>) =>
  axios.post<ApiRes<PendingRecordDto>>('/api/trung-tam/pending-records', data).then(r => r.data);

export const updatePendingRecordApi = (id: number, data: Partial<PendingRecordDto>) =>
  axios.put<ApiRes<PendingRecordDto>>(`/api/trung-tam/pending-records/${id}`, data).then(r => r.data);

export const listPendingRecordsApi = () =>
  axios.get<ApiRes<PendingRecordDto[]>>('/api/trung-tam/pending-records').then(r => r.data);

export const getPendingRecordDetailApi = (id: number) =>
  axios.get<ApiRes<PendingRecordDto>>(`/api/trung-tam/pending-records/${id}`).then(r => r.data);

export const deletePendingRecordApi = (id: number) =>
  axios.delete<ApiRes<{ deletedId: number }>>(`/api/trung-tam/pending-records/${id}`).then(r => r.data);

export const clearAllPendingRecordsApi = () =>
  axios.delete<ApiRes<{ deleted: number }>>('/api/trung-tam/pending-records').then(r => r.data);

export type SupperTeacherOption = {
  id: number;
  fullName: string;
  email: string;
};

export const listSupperTeachersApi = () =>
  axios.get<ApiRes<SupperTeacherOption[]>>('/api/trung-tam/supper-teachers').then(r => r.data);

export const checkCccdDuplicateApi = (soCCCD: string) =>
  axios.get<ApiRes<{ duplicates: DuplicateInfo[] }>>(
    `/api/trung-tam/pending-records/check-duplicate`,
    { params: { soCCCD } }
  ).then(r => r.data);

export const savePendingRecordApi = (
  id: number,
  payload: { courseId: number; overrides?: Record<string, unknown> }
) =>
  axios.post<ApiRes<{ record: TtStudentRecord }>>(
    `/api/trung-tam/pending-records/${id}/save`,
    payload
  ).then(r => r.data);

export type RedetectPendingResult = {
  record: PendingRecordDto;
  cccdEmpty: boolean;
  gplxEmpty: boolean;
  cccdEngine: string | null;
  gplxEngine: string | null;
  cccdError: string | null;
  gplxError: string | null;
};

/**
 * Re-run OCR on the images already stored on a pending row. Used when the
 * first detect attempt failed (eg Gemini 503 high-demand) so the operator
 * doesn't have to re-upload photos. Backend overrides only NULL/empty
 * fields — anything the operator typed by hand is preserved.
 */
export const redetectPendingApi = (id: number) =>
  axios.post<ApiRes<RedetectPendingResult>>(
    `/api/trung-tam/pending-records/${id}/redetect`,
    {},
    { timeout: 90_000 }
  ).then(r => r.data);

/**
 * Called when the server returned DUPLICATE on save and the operator
 * confirmed the "Chuyển hồ sơ sang khóa mới" dialog. Moves the
 * existing student record to the new course instead of creating a
 * second record for the same CCCD.
 */
export const transferExistingRecordApi = (
  pendingId: number,
  payload: { courseId: number; overrides?: Record<string, unknown> }
) =>
  axios.post<ApiRes<{
    recordId: number;
    soCCCD: string;
    movedFromCourseId: number | null;
    movedFromCourseName: string | null;
    movedToCourseId: number;
    movedToCourseName: string;
  }>>(
    `/api/trung-tam/pending-records/${pendingId}/transfer-existing`,
    payload
  ).then(r => r.data);

export type BulkTransferPendingResult = {
  transferred: number;
  skipped: Array<{ id: number; soCCCD: string | null; reason: string }>;
  targetCourseId: number;
  targetCourseName: string;
  targetCourseHang: string;
};

/**
 * Move a batch of pending rows to another open course.
 * Used by: (a) the bulk action in PendingRecordsTab.
 */
export const bulkTransferPendingApi = (pendingIds: number[], targetCourseId: number) =>
  axios.post<ApiRes<BulkTransferPendingResult>>(
    '/api/trung-tam/pending-records/bulk-transfer',
    { pendingIds, targetCourseId }
  ).then(r => r.data);

// ── Teacher (SupperTeacher) list for record assignment ──────────────────────

export type SupperTeacherItem = { id: number; username: string; email: string };

/** Lightweight list of active supper teachers for the record-assignment dropdown. */
export const fetchSupperTeachers = () =>
  axios.get<ApiRes<SupperTeacherItem[]>>('/api/trung-tam/supper-teachers').then(r => r.data);
